import { useCallback, useEffect, useState } from "react";
import type { CheckoutProductProps } from "@baluu/ui/components/checkout";
import { fetchSchoolCheckoutConfig } from "~/widgets/lib/school-checkout-config";

export type TermsSettings = NonNullable<CheckoutProductProps["termsSettings"]>;
export type CustomTerms = NonNullable<CheckoutProductProps["customTerms"]>;

/** The part of `GET /widget/school/:id/checkout-config` the purchase
 *  overlays read (the basket checkout reads the same, shared, answer). */
export type SchoolCheckoutConfig = {
  name: string;
  slug: string;
  timezone: string;
  preferredCurrency: string;
  termsSettings?: TermsSettings;
  customTerms?: CustomTerms | null;
  cancellationPolicyUrl?: string;
  businessCountry?: string;
  isNewsletterSignupEnabled?: boolean;
  requireLoginToBook: boolean;
  /** The school's Basket Checkout setting; `isEnabled: false` makes every
   *  surface single-item (basketModeFor). */
  basket?: { isEnabled?: boolean };
};

/** The settings, and whether they're here yet. Until they are — and if they
 *  can't be had — nothing that depends on them (whether the school asks for
 *  a sign-in, its name on the basket line) may be assumed: callers wait on
 *  "loading" and offer `retry` on "error". */
export type SchoolCheckoutConfigState<T = SchoolCheckoutConfig> =
  | { status: "loading"; config: null; retry: () => void }
  | { status: "error"; config: null; retry: () => void }
  | { status: "ready"; config: T; retry: () => void };

/** The school's checkout settings (`T`: the fields a caller reads beyond
 *  the shared ones — the basket checkout reads more). */
export function useSchoolCheckoutConfig<T = SchoolCheckoutConfig>(
  businessId: string,
  apiUrl?: string,
): SchoolCheckoutConfigState<T> {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  // Each answer is kept with the request it answers, so a new request (a
  // retry, another school) reads as loading until its own answer lands.
  const key = `${businessId}|${apiUrl ?? ""}|${attempt}`;
  const [answer, setAnswer] = useState<{ key: string; config: T | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSchoolCheckoutConfig<Record<string, unknown>>(businessId, apiUrl)
      .then((data) => {
        if (!cancelled) {
          setAnswer({ key, config: { ...data, requireLoginToBook: Boolean(data.requireLoginToBook) } as T });
        }
      })
      .catch(() => {
        if (!cancelled) setAnswer({ key, config: null });
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, apiUrl, key]);

  if (answer?.key !== key) return { status: "loading", config: null, retry };
  return answer.config ? { status: "ready", config: answer.config, retry } : { status: "error", config: null, retry };
}
